// VerifyEmail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService, { errorHelpers } from '../../utils/apiService';

const VerifyEmail = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [verificationState, setVerificationState] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('');
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        verifyEmailToken();
    }, [token]);

    useEffect(() => {
        if (verificationState === 'success' && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (verificationState === 'success' && countdown === 0) {
            navigate('/');
        }
    }, [verificationState, countdown, navigate]);

    const verifyEmailToken = async () => {
        try {
            const response = await apiService.auth.verifyEmail(token);
            setVerificationState('success');
            setMessage(response.data.message || 'Email verified successfully!');
        } catch (error) {
            setVerificationState('error');
            const errorMessage = errorHelpers.getErrorMessage(error);
            setMessage(errorMessage);
        }
    };

    const handleGoHome = () => {
        navigate('/');
    };

    return (
        <div className="verify-email-page">
            <div className="verify-container">
                <div className="verify-card">
                    {/* Logo Section */}
                    <div className="verify-logo">
                        <div className="logo-icon">OU</div>
                        <h1>Olabisi Onabanjo University</h1>
                        <p>Certificate Verification System</p>
                    </div>

                    {/* Verification Content */}
                    <div className="verify-content">
                        {verificationState === 'verifying' && (
                            <div className="verify-loading">
                                <div className="loading-spinner-large"></div>
                                <h2>Verifying Your Email</h2>
                                <p>Please wait while we verify your email address...</p>
                            </div>
                        )}

                        {verificationState === 'success' && (
                            <div className="verify-success">
                                <div className="success-icon">✅</div>
                                <h2>Email Verified Successfully!</h2>
                                <p className="success-message">{message}</p>
                                <div className="success-details">
                                    <p>Your email has been verified and your account is now active.</p>
                                    <p>You can now log in and start using the certificate verification system.</p>
                                </div>
                                <div className="redirect-info">
                                    <p>Redirecting to home page in {countdown} seconds...</p>
                                    <button onClick={handleGoHome} className="btn btn-primary">
                                        Go to Home Page
                                    </button>
                                </div>
                            </div>
                        )}

                        {verificationState === 'error' && (
                            <div className="verify-error">
                                <div className="error-icon">❌</div>
                                <h2>Email Verification Failed</h2>
                                <p className="error-message">{message}</p>
                                <div className="error-details">
                                    <p>This could happen if:</p>
                                    <ul>
                                        <li>The verification link has expired</li>
                                        <li>The verification link has already been used</li>
                                        <li>The verification link is invalid</li>
                                    </ul>
                                </div>
                                <div className="error-actions">
                                    <button onClick={handleGoHome} className="btn btn-primary">
                                        Go to Home Page
                                    </button>
                                    <p className="help-text">
                                        Need help? Contact support or try registering again.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Styles */}
            <style jsx>{`
        .verify-email-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .verify-container {
          width: 100%;
          max-width: 500px;
        }

        .verify-card {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .verify-logo {
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          text-align: center;
          padding: 3rem 2rem 2rem;
        }

        .logo-icon {
          width: 60px;
          height: 60px;
          background: white;
          color: var(--primary-blue);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0 auto 1rem;
        }

        .verify-logo h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .verify-logo p {
          opacity: 0.9;
          margin: 0;
        }

        .verify-content {
          padding: 3rem 2rem;
          text-align: center;
        }

        .loading-spinner-large {
          width: 60px;
          height: 60px;
          border: 4px solid var(--gray-200);
          border-top: 4px solid var(--secondary-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 2rem;
        }

        .verify-loading h2,
        .verify-success h2,
        .verify-error h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: var(--gray-800);
        }

        .verify-loading p {
          color: var(--gray-600);
          margin-bottom: 2rem;
        }

        .success-icon,
        .error-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
        }

        .success-message,
        .error-message {
          font-size: 1.1rem;
          margin-bottom: 2rem;
          padding: 1rem;
          border-radius: 0.5rem;
        }

        .success-message {
          background: var(--green-50);
          color: var(--success-green);
          border: 1px solid var(--green-200);
        }

        .error-message {
          background: var(--red-50);
          color: var(--error-red);
          border: 1px solid var(--red-200);
        }

        .success-details,
        .error-details {
          text-align: left;
          background: var(--gray-50);
          padding: 1.5rem;
          border-radius: 0.5rem;
          margin-bottom: 2rem;
        }

        .success-details p,
        .error-details p {
          margin-bottom: 0.5rem;
          color: var(--gray-700);
        }

        .success-details p:last-child,
        .error-details p:last-child {
          margin-bottom: 0;
        }

        .error-details ul {
          margin: 1rem 0;
          padding-left: 1.5rem;
          color: var(--gray-600);
        }

        .error-details li {
          margin-bottom: 0.5rem;
        }

        .redirect-info,
        .error-actions {
          margin-top: 2rem;
        }

        .redirect-info p {
          color: var(--gray-600);
          margin-bottom: 1rem;
          font-size: 0.875rem;
        }

        .help-text {
          font-size: 0.875rem;
          color: var(--gray-500);
          margin-top: 1rem;
        }

        .btn {
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          font-size: 1rem;
          font-weight: 500;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-block;
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 640px) {
          .verify-email-page {
            padding: 1rem;
          }

          .verify-content {
            padding: 2rem 1.5rem;
          }

          .verify-logo {
            padding: 2rem 1.5rem 1.5rem;
          }

          .verify-logo h1 {
            font-size: 1.25rem;
          }
        }
      `}</style>
        </div>
    );
};

export default VerifyEmail;